<template>
  <div
    id="floating"
    ref="menuRef"
    class="flex absolute h-38 max-w-calc-50 w-full md:w-auto z-20 mb-8 editor__floating-menu"
    :class="{ 'is-active': isActive && (editor.isFocused || showBlockColorPicker || showMoreMenu || showGifPicker) }"
    :style="`top: ${menuTop + 25}px`"
  >
    <div
      class="no-scroll-bar rounded-4 z-20 shadow-menu overflow-x-auto flex opacity-90 hover:opacity-100"
      :class="isDarkMode ? 'bg-sidebar' : 'bg-primary'"
    >
      <MenuButtton
        :ref="el => refs[0] = el"
        class="text-13"
        :is-active="activeIndex === 0"
        @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
      >
        H1
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[1] = el"
        class="text-13"
        :is-active="activeIndex === 1"
        @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
      >
        H2
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[2] = el"
        class="text-13 border-r border-hover"
        :is-active="activeIndex === 2"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
      >
        H3
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[3] = el"
        class="flex justify-center items-center"
        :is-active="activeIndex === 3"
        @click="editor.chain().focus().toggleTaskList().run()"
      >
        <i class="icon-checkbox text-19" />
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[4] = el"
        class="flex justify-center items-center"
        :is-active="activeIndex === 4"
        @click="openImageUploader"
      >
        <i class="icon-image text-16 mt-2" />
        <input ref="inputRef" class="hidden" type="file" @change="upload">
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[5] = el"
        class="flex justify-center items-center"
        :is-active="activeIndex === 5"
        @click="editor.chain().focus().setHorizontalRule().run()"
      >
        <i class="icon-divider text-2" />
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[6] = el"
        class="border-r border-hover flex justify-center items-center relative"
        :is-active="showBlockColorPicker || activeIndex === 6"
        @click.stop="openBlockColorPicker"
      >
        <i class="icon-block-color text-19" />
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[7] = el"
        class="text-18"
        :is-active="activeIndex === 7"
        @click="onUndo"
      >
        <i class="icon-undo text-15" />
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[8] = el"
        class="text-18 text-app-color"
        :is-active="activeIndex === 8"
        @click="onRedo"
      >
        <i class="icon-redo text-15" />
      </MenuButtton>

      <MenuButtton
        :ref="el => refs[9] = el"
        class="text-18 text-app-color flex justify-center items-center"
        :is-active="activeIndex === 9"
        @click.stop="openMoreMenu"
      >
        <i class="icon-more-horizontal text-21" />
      </MenuButtton>
    </div>

    <ButtonModule
      prefix="icon-close-rounded text-20"
      class="text-app-color cursor-pointer ml-5 text-12 text-grey-300 hover:text-app-color"
      @click.stop="close"
    />

    <div
      v-show="showMoreMenu"
      ref="menuMoreRef"
      class="absolute z-10 bg-primary rounded-4 shadow text-app-color"
      :style="menuPosition"
    >
      <div class="hover:bg-selection rounded-t-4 group-scope">
        <ButtonModule
          text="Table"
          :prefix="`icon-table mt-2 mr-12 text-16 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
          class="py-9 px-12 w-full text-14"
          @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
        />
      </div>
      <div class="hover:bg-selection group-scope">
        <ButtonModule

          class="py-9 px-12 w-full text-14"
          @click="editor.chain().focus().toggleCodeBlock().run()"
        >
          <span
            class="mr-9 text-12"
            :class="`${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
          >{{ codeIcon }}</span> Code block
        </ButtonModule>
      </div>
      <div class="hover:bg-selection group-scope">
        <ButtonModule
          text="Upload file"
          :prefix="`icon-attach-file mr-12 text-16 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
          class="py-9 px-12 w-full text-14"
          @click="openImageUploader"
        />
      </div>
      <div class="hover:bg-selection rounded-b-4 group-scope">
        <ButtonModule
          class="py-9 px-12 w-full"
          @click.stop="openGifPicker"
        >
          <span
            class="text-12 font-bold mr-9 mt-3"
            :class="`${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
          >GIF</span>
          <span class="text-14">Giphy</span>
        </ButtonModule>
      </div>
    </div>

    <div
      v-show="showBlockColorPicker"
      ref="colorRef"
      class="absolute z-10 bg-primary rounded-4 shadow"
      :style="blockColorPosition"
    >
      <div class="cursor-default">
        <p class="tex-16 font-bold text-left text-app-color pl-10 pt-5">Pick a block color</p>
        <div class="w-225 flex flex-wrap m-6">
          <div
            v-for="(c, index) in colors"
            :key="c + index"
            class="w-37 h-37 rounded-full mx-4 my-5 cursor-pointer"
            :style="`background-color: ${c}`"
            @click.stop="applyBlockColor(c)"
          />
        </div>
        <div v-if="editor.isActive('ColorBlock')" class="flex items-center p-10 pt-0 text-11 text-app-color opacity-50 hover:opacity-100 cursor-pointer" @click.stop="removeBlockColor()">
          <i class="icon-remove mr-5" />
          <p>Remove color</p>
        </div>
      </div>
    </div>
    <!-- gif picker -->
    <GifPicker
      v-if="showGifPicker"
      ref="gifPickerRef"
      v-clickOutside="closeGifPicker"
      class="top-50 md:max-w-full"
      @selectGif="insertGif"
      @click.stop
    />
  </div>
</template>

<script>
import FloatingMenu from '@/tiptap/plugins/FloatingMenu'
import { attachmentService } from '@/services'

import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { getPosition } from '@/core/helper-functions'

import ButtonModule from '@/components/shared/form-controls/ButtonModule'
import GifPicker from '@/components/shared/GifPicker'
import AWS from 'aws-sdk'
import MenuButtton from '@/components/workspace/MenuButton.vue'
import defaultColors from '@/core/colors'

export default {
  components: { ButtonModule, GifPicker, MenuButtton },

  props: {
    editor: { type: Object }
  },

  data () {
    return {
      showBlockColorPicker: false,
      showMoreMenu: false,
      showGifPicker: false,
      blockColorPosition: {},
      menuPosition: {},
      color: null,
      activeIndex: -1,
      colors: defaultColors.backgroundColors,
      menuTop: 0,
      isActive: false,
      refs: [],
      codeIcon: '< >'
    }
  },

  computed: {
    ...mapState({
      share: state => state.project.share,
      currentProject: state => state.project.currentProject
    }),

    ...mapGetters(['isMobile', 'isDarkMode', 'allowAddAttachments', 'isTwilightTheme'])
  },

  watch: {
    editor: {
      immediate: true,
      handler (editor) {
        if (editor) {
          this.$nextTick(() => {
            editor.registerPlugin(FloatingMenu({
              editor,
              element: this.$el,
              onUpdate: menu => {
                this.isActive = menu.isActive
                this.menuTop = menu.top
              }
            }))
          })
        }
      }
    },

    isActive (value) {
      this.activeIndex = -1

      // Mobile: wait for the keyboard to open
      // and scroll menu to position above the keyboard
      if (value && this.isMobile) {
        setTimeout(() => {
          this.$refs.menuRef.scrollIntoView(false)
        }, 300)
      }
    }
  },

  mounted () {
    window.addEventListener('keydown', this.arrowNavigation)
  },

  // to reset the refs before each update
  beforeUpdate () {
    this.refs = []
  },

  unmounted () {
    window.removeEventListener('keydown', this.arrowNavigation)
  },

  methods: {
    ...mapActions(['stats', 'updateFile']),
    ...mapMutations(['loading', 'toggleLimitModal', 'setNotification', 'setFile']),

    arrowNavigation (e) {
      if (this.isActive) {
        if (e.keyCode === 37) {
          e.preventDefault()
          if (this.activeIndex > 0) this.activeIndex--
          else this.activeIndex = 10
        }
        if (e.keyCode === 39) {
          e.preventDefault()
          if (this.activeIndex < 10) this.activeIndex++
          else this.activeIndex = 0
        }
        if (e.keyCode === 13 && this.activeIndex >= 0) {
          e.preventDefault()
          this.editor.chain().focus().undo().run()
          this.refs[this.activeIndex].$el.click()
        }
      }
    },

    showImagePrompt (command) {
      const src = prompt('Enter the url of your image here')
      if (src !== null) {
        command({ src })
      }
    },

    closeBlockColorPicker () {
      this.showBlockColorPicker = false
      this.showMoreMenu = false
    },

    removeBlockColor () {
      this.showBlockColorPicker = false
      this.editor.chain().focus().setColorBlock().run()
    },

    applyBlockColor (color) {
      this.showBlockColorPicker = false
      this.editor.chain().focus().setColorBlock({ style: `background-color: ${color}` }).run()
    },

    openImageUploader () {
      this.$refs.inputRef.click()
    },

    async upload (event) {
      const hasFiles = event.target.files.length

      if (!hasFiles) return

      const files = Array.from(event.target.files)

      if (files.length === 0) return

      const file = files[0]
      const isImages = (/image/i).test(file.type)

      if (file.size / 1024 / 1024 >= 400) {
        return this.setNotification({ isVisible: true, message: 'File size too big, uploads up to 400MB are allowed', color: 'error' })
      }

      if (isImages) {
        const image = await this.imageUpload(file)
        this.editor.chain().focus().setImage(image).run()
      } else {
        const data = await this.uploadToS3(file)
        this.editor.chain().focus().setFile(data).run()
      }

      event.srcElement.value = ''
    },

    async uploadToS3 (file) {
      const projectId = this.currentProject.id

      AWS.config.update({
        region: 'eu-central-1',
        credentials: new AWS.CognitoIdentityCredentials({
          IdentityPoolId: 'eu-central-1:6c25ba0f-af55-402a-896a-c52812ec3398'
        })
      })

      var s3 = new AWS.S3({
        apiVersion: '2006-03-01',
        params: { Bucket: process.env.VUE_APP_AWS_BUCKET }
      })

      const options = {
        projectId: projectId,
        shareId: this.share.id,
        mimeType: file.type || 'application/octet-stream',
        size: file.size,
        name: file.name
      }
      const methodName = this.$route.name === 'Share' ? 'createFileShared' : 'createFile'

      const uploadedFile = await attachmentService[methodName](options)

      this.setFile({
        id: uploadedFile.data.id,
        percent: 1,
        bookId: this.currentProject.bookId
      })

      const request = s3.putObject({
        Key: uploadedFile.data.key,
        Body: file,
        ACL: 'public-read'
      })
        .on('httpUploadProgress', (evt) => {
          this.setFile({
            id: uploadedFile.data.id,
            percent: parseInt((evt.loaded * 100) / evt.total) || 1,
            bookId: this.currentProject.bookId,
            abort: () => setTimeout(() => request.abort(), 50)
          })
        })

      const url = `https://${process.env.VUE_APP_AWS_BUCKET}.s3.eu-central-1.amazonaws.com/${uploadedFile.data.key}`

      request.promise().then((data) => {
        this.setFile({ id: uploadedFile.data.id, percent: -1, bookId: this.currentProject.bookId, request: request })

        const method = this.$route.name === 'Share' ? 'updateFileShared' : 'updateFile'

        attachmentService[method]({
          id: uploadedFile.data.id,
          shareId: this.share.id,
          projectId: projectId,
          url: url
        })
      }).catch(() => {})

      return {
        id: uploadedFile.data.id,
        name: file.name,
        size: file.size
      }
    },

    async imageUpload (file) {
      this.loading(true)

      if (this.$route.name === 'Share') {
        const formData = new FormData()
        formData.append('data', file)

        return attachmentService.uploadShared(this.currentProject.id, this.share.id, formData)
          .then(response => {
            return {
              url: response.data.url,
              id: response.data.id,
              name: file.name,
              size: file.size
            }
          })
      } else {
        if (this.allowAddAttachments(file.size)) {
          const formData = new FormData()
          formData.append('data', file)

          return attachmentService.upload(this.currentProject.id, formData)
            .then(response => {
              this.stats()
              return {
                url: response.data.url,
                id: response.data.id,
                name: file.name,
                size: file.size
              }
            })
        } else {
          this.toggleLimitModal({ show: true, type: 'File uploads' })
          this.loading(false)
        }
      }
    },

    openBlockColorPicker () {
      this.showBlockColorPicker = !this.showBlockColorPicker
      this.showMoreMenu = false

      this.$nextTick(() => {
        this.blockColorPosition = getPosition(this.$refs.menuRef, this.$refs.colorRef, this.refs[6].$el)
      })
    },

    openMoreMenu () {
      this.showMoreMenu = !this.showMoreMenu
      this.showBlockColorPicker = false

      this.$nextTick(() => {
        this.menuPosition = getPosition(this.$refs.menuRef, this.$refs.menuMoreRef, this.refs[9].$el)
      })
    },

    onUndo () {
      this.editor.chain().focus().undo().run()
      if (this.isMobile) {
        this.isActive = true
      }
    },

    onRedo () {
      this.editor.chain().focus().redo().run()
      if (this.isMobile) this.isActive = true
    },

    openGifPicker () {
      this.showGifPicker = !this.showGifPicker
      this.showMoreMenu = false

      setTimeout(() => {
        if (this.$refs.gifPickerRef) this.$refs.gifPickerRef.toggleInput()
      }, 100)
    },

    insertGif (gif) {
      //
      this.editor.chain().focus().setImage({
        id: null,
        url: gif
      }).run()
      this.closeGifPicker()
    },

    closeGifPicker () {
      this.showGifPicker = false
    },

    close () {
      this.isActive = false
      this.editor.chain().focus()
    }
  }
}
</script>
